
/**
 * Service for fetching public system statistics and analytics data.
 */
import unauthenticatedApi from "@/utils/unauthenticatedAxios";

export type StatisticsSummaryDto = {
  totalWebsites: number;
  totalCategories: number;
  avgCategoriesPerWebsite: number;
  avgCategoryDepthPerWebsite: number;
  websitesLast30Days: number;
  totalUsers: number;
  categoryDistribution: { slug: string; nameDe: string; nameEn: string; count: number }[];
  categoryDepthCoverage: { depth: number; count: number }[];
  regionDistribution: { name: string; count: number }[];
  monthlyTrends: { month: string; websites: number; users: number }[];
};

export type StatisticsOverviewDto = {
  totalWebsites: number;
  totalCategories: number;
  totalUsers: number;
  websitesLast30Days: number;
  avgCategoriesPerWebsite: number;
  avgCategoryDepthPerWebsite: number;
  lastWebsiteAddedAt: string | null;
  lastUserRegisteredAt: string | null;
  topCategory: {
    slug: string;
    nameDe: string;
    nameEn: string;
    count: number;
    sharePercent: number;
  } | null;
};

export type StatisticsGrowthDto = {
  monthlyTrends: { month: string; websites: number; users: number }[];
  websitesLast7Days: number;
  websitesLast30Days: number;
  websitesPrevious7Days: number;
  usersLast7Days: number;
  usersLast30Days: number;
  usersPrevious7Days: number;
};

export type StatisticsCategoriesDto = {
  totalWebsites: number;
  totalCategories: number;
  avgCategoriesPerWebsite: number;
  avgCategoryDepthPerWebsite: number;
  categoryDistribution: {
    slug: string;
    nameDe: string;
    nameEn: string;
    count: number;
  }[];
  categoryDepthCoverage: { depth: number; count: number }[];
};

export type StatisticsRegionLayer = "state" | "administrativeDistrict" | "district" | "postalCode";

export type StatisticsRegionsDto = {
  layer: StatisticsRegionLayer;
  totalInLayer: number;
  items: {
    name: string;
    stateName: string;
    count: number;
  }[];
};

export type StatisticsEngagementDto = {
  days: number;
  dailyPageviews: DailyPageviewsPoint[];
  pageviewsLast7Days: number;
  pageviewsLast30Days: number;
  avgDailyPageviewsLast7Days: number;
  peakPageviewsDay: DailyPageviewsPoint;
  websitesLast7Days: number;
  websitesLast30Days: number;
  usersLast7Days: number;
  usersLast30Days: number;
};

export async function fetchStatisticsSummary() {
  const res = await unauthenticatedApi.get<StatisticsSummaryDto>(
    "/public/statistics/summary"
  );
  return res.data;
}

export async function fetchStatisticsOverview() {
  const res = await unauthenticatedApi.get<StatisticsOverviewDto>(
    "/public/statistics/overview"
  );
  return res.data;
}

export async function fetchStatisticsGrowth() {
  const res = await unauthenticatedApi.get<StatisticsGrowthDto>(
    "/public/statistics/growth"
  );
  return res.data;
}

export async function fetchStatisticsCategories() {
  const res = await unauthenticatedApi.get<StatisticsCategoriesDto>(
    "/public/statistics/categories"
  );
  return res.data;
}

export async function fetchStatisticsRegions(
  layer: StatisticsRegionLayer,
  limit = 20
) {
  const safeLimit = Math.max(5, Math.min(200, Math.floor(limit)));
  const res = await unauthenticatedApi.get<StatisticsRegionsDto>(
    `/public/statistics/regions?layer=${encodeURIComponent(
      layer
    )}&limit=${encodeURIComponent(String(safeLimit))}`
  );
  return res.data;
}

export async function fetchStatisticsEngagement(days = 30) {
  const safeDays = Math.max(7, Math.min(365, Math.floor(days)));
  const res = await unauthenticatedApi.get<StatisticsEngagementDto>(
    `/public/statistics/engagement?days=${encodeURIComponent(String(safeDays))}`
  );
  return res.data;
}

export interface DailyPageviewsPoint {
  date: string; // yyyy-mm-dd
  pageviews: number;
}

export async function fetchDailyPageviews(
  days = 30
): Promise<DailyPageviewsPoint[]> {
  const safeDays = Math.max(1, Math.min(365, Math.floor(days)));

  const url = `/public/statistics/pageviews/daily?days=${encodeURIComponent(
    String(safeDays)
  )}`;

  const res = await unauthenticatedApi.get<DailyPageviewsPoint[]>(url, {
    headers: { Accept: "application/json", "Cache-Control": "no-store" },
    validateStatus: () => true,
  });

  if (res.status < 200 || res.status >= 300) {
    const serverMessage = (res.data as any)?.message;
    throw new Error(
      typeof serverMessage === "string" && serverMessage.trim()
        ? serverMessage
        : `Daily pageviews request failed with status ${res.status}`
    );
  }

  return Array.isArray(res.data) ? res.data : [];
}
